import { FC, useRef } from 'react';
import { Button, Input } from 'antd';
import type { InputRef } from 'antd';

import Modal from '../../../components/Modal/Modal';
import { ListenerFullRow } from '../CourseEnrollment.const';
import { FormUpdater } from '../CourseEnrollment.types';
import {
  formatPassportDate,
  isValidPassportDate,
  isValidEmail,
} from '../CourseEnrollment.utils';
import css from '../CourseEnrollment.module.css';

interface Props {
  open: boolean;
  isEditMode: boolean;
  listener: ListenerFullRow | null;
  onChange: FormUpdater<ListenerFullRow>;
  onClose: () => void;
  onSave: () => void;
  saving?: boolean;
}

const BY_CHARS = ['і', 'ў', '’'] as const;
const BY_CHAR_LABELS: Record<string, string> = { '’': "'" };

const ListenerModal: FC<Props> = ({
  open,
  isEditMode,
  listener,
  onChange,
  onClose,
  onSave,
  saving,
}) => {
  const byInputRef = useRef<InputRef>(null);

  const insertByChar = (char: string) => {
    const input = byInputRef.current?.input;
    if (!input || !listener) return;

    const start = input.selectionStart ?? listener.fullNameBy.length;
    const end = input.selectionEnd ?? start;
    const next = listener.fullNameBy.slice(0, start) + char + listener.fullNameBy.slice(end);
    onChange('fullNameBy', next);

    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(start + char.length, start + char.length);
    });
  };

  return (
    <Modal
      display={open}
      onClose={onClose}
      title={isEditMode ? 'Редактирование слушателя' : 'Новый слушатель'}
    >
      {listener && (
        <div className={css.ListenerModal}>
          <div className={css.FormGrid}>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>ФИО на русском *</span>
              <Input
                placeholder='Иванов Иван Иванович'
                value={listener.fullNameRu}
                onChange={(e) => onChange('fullNameRu', e.target.value)}
              />
            </div>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>ФИО на белорусском *</span>
              <Input
                ref={byInputRef}
                placeholder='Іванаў Іван Іванавіч'
                value={listener.fullNameBy}
                onChange={(e) => onChange('fullNameBy', e.target.value)}
              />
              <div className={css.ByCharButtons}>
                {BY_CHARS.map((char) => (
                  <button
                    key={char}
                    type='button'
                    className={css.ByCharBtn}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertByChar(char)}
                  >
                    {BY_CHAR_LABELS[char] ?? char}
                  </button>
                ))}
              </div>
            </div>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>Адрес *</span>
              <Input
                placeholder='г. Минск, ул. ..., д. ..., кв. ...'
                value={listener.address}
                onChange={(e) => onChange('address', e.target.value)}
              />
            </div>
            <div className={css.Field}>
              <span className={css.FieldLabel}>Серия и номер паспорта *</span>
              <Input
                placeholder='MP1234567'
                value={listener.passport}
                onChange={(e) => onChange('passport', e.target.value)}
              />
            </div>
            <div className={css.Field}>
              <span className={css.FieldLabel}>Дата выдачи *</span>
              <Input
                placeholder='ДД.ММ.ГГГГ'
                value={listener.passportIssueDate}
                maxLength={10}
                status={
                  listener.passportIssueDate && !isValidPassportDate(listener.passportIssueDate)
                    ? 'error'
                    : undefined
                }
                onChange={(e) => onChange('passportIssueDate', formatPassportDate(e.target.value))}
              />
            </div>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>Кем выдан *</span>
              <Input
                placeholder='УВД г. Минска'
                value={listener.passportIssuer}
                onChange={(e) => onChange('passportIssuer', e.target.value)}
              />
            </div>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>E-mail для доступа *</span>
              <Input
                placeholder='example@mail.com'
                value={listener.externalEmail}
                status={
                  listener.externalEmail && !isValidEmail(listener.externalEmail)
                    ? 'error'
                    : undefined
                }
                onChange={(e) => onChange('externalEmail', e.target.value)}
              />
            </div>
          </div>

          <div className={css.ModalFooter}>
            <Button className={css.BackBtn} onClick={onClose} disabled={saving}>
              Отмена
            </Button>
            <Button type='primary' className={css.NextBtn} onClick={onSave} loading={saving} disabled={saving}>
              {isEditMode ? 'Сохранить' : 'Добавить'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ListenerModal;
